import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { billing, identity } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Stack, Card, Title, Text, Group, Skeleton, Button } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Checkbox, Switch } from '@repletoryreach/sdk-ui/inputs';
import { notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { PushDeviceCard } from './PushDeviceCard';

/*
 * One stored preference is either a plain on/off for the whole notification
 * or an object of channel switches:
 *
 *   (absent)                                on, every channel
 *   false                                   off entirely
 *   true                                    on, every channel
 *   { email: true, whatsapp: false }        on, only the channels named true
 *
 * The column has always been able to hold the second shape and the console
 * only ever wrote the first, which is how the channel choice came to be
 * stored and ignored. The dispatcher that now reads it lives in
 * services/billing/Services/Alerts/NotificationRouting.cs.
 */
type ChannelMap = Record<string, boolean>;
type PrefValue = identity.NotificationPrefValue;
type Prefs = identity.NotificationPrefs;

/*
 * The kinds and the channels come from the server's `meta`, so a new
 * notification appears here without a redeploy. These lists are only the
 * fallback for an older service that does not send them.
 */
const FALLBACK_CHANNELS = ['email', 'whatsapp', 'webhook', 'in_app'];
/** Push reaches the notices below (9 October 2026); billing does not push the wallet alerts, so they never offer it. */
const NOT_FOR_WALLET = new Set(['push']);
const FALLBACK_KINDS = [
  'low_balance', 'depleted', 'recovered', 'daily_spend', 'auto_topup_succeeded', 'auto_topup_failed',
  'broadcast_complete', 'delivery_failures', 'weekly_digest', 'new_conversation'
];

/*
 * Friendly words for the kinds we know about. `key` is the stored pref name
 * (data, never translated); the label and hint are catalog keys resolved at
 * render, because this table is module scope and cannot call the hook. A kind
 * the platform sends that is not in here renders under its own name rather
 * than not at all.
 */
const KIND_COPY: Record<string, { labelKey: string; hintKey: string }> = {
  low_balance: { labelKey: 'settings.notifications.toggle.lowBalance.label', hintKey: 'settings.notifications.toggle.lowBalance.hint' },
  depleted: { labelKey: 'settings.notifications.toggle.depleted.label', hintKey: 'settings.notifications.toggle.depleted.hint' },
  recovered: { labelKey: 'settings.notifications.toggle.recovered.label', hintKey: 'settings.notifications.toggle.recovered.hint' },
  daily_spend: { labelKey: 'settings.notifications.toggle.dailySpend.label', hintKey: 'settings.notifications.toggle.dailySpend.hint' },
  auto_topup_succeeded: { labelKey: 'settings.notifications.toggle.autoTopupSucceeded.label', hintKey: 'settings.notifications.toggle.autoTopupSucceeded.hint' },
  auto_topup_failed: { labelKey: 'settings.notifications.toggle.autoTopupFailed.label', hintKey: 'settings.notifications.toggle.autoTopupFailed.hint' },
  // Extra use on Growth and Scale (30 October 2026).
  extra_use_started: { labelKey: 'settings.notifications.toggle.extraUseStarted.label', hintKey: 'settings.notifications.toggle.extraUseStarted.hint' },
  extra_use_charged: { labelKey: 'settings.notifications.toggle.extraUseCharged.label', hintKey: 'settings.notifications.toggle.extraUseCharged.hint' },
  extra_use_declined: { labelKey: 'settings.notifications.toggle.extraUseDeclined.label', hintKey: 'settings.notifications.toggle.extraUseDeclined.hint' },
  extra_use_limit: { labelKey: 'settings.notifications.toggle.extraUseLimit.label', hintKey: 'settings.notifications.toggle.extraUseLimit.hint' },
  broadcast_complete: { labelKey: 'settings.notifications.toggle.broadcast.label', hintKey: 'settings.notifications.toggle.broadcast.hint' },
  delivery_failures: { labelKey: 'settings.notifications.toggle.failures.label', hintKey: 'settings.notifications.toggle.failures.hint' },
  weekly_digest: { labelKey: 'settings.notifications.toggle.digest.label', hintKey: 'settings.notifications.toggle.digest.hint' },
  new_conversation: { labelKey: 'settings.notifications.toggle.newConversation.label', hintKey: 'settings.notifications.toggle.newConversation.hint' },
  // Statements and template decisions became switchable with the
  // transactional-email work; the partner ones came with W47.
  statement_ready: { labelKey: 'settings.notifications.toggle.statement.label', hintKey: 'settings.notifications.toggle.statement.hint' },
  template_decision: { labelKey: 'settings.notifications.toggle.templateDecision.label', hintKey: 'settings.notifications.toggle.templateDecision.hint' },
  affiliate_new_referral: { labelKey: 'settings.notifications.toggle.newReferral.label', hintKey: 'settings.notifications.toggle.newReferral.hint' },
  affiliate_commission_earned: { labelKey: 'settings.notifications.toggle.earnings.label', hintKey: 'settings.notifications.toggle.earnings.hint' }
};

/*
 * The inbox, Bookings and Tickets notices, shown in groups of their own.
 * Unlike the kinds above they do not start on every channel: each starts on
 * the channels in `billing.NOTICE_KIND_DEFAULTS`, which is the rule the
 * dispatcher applies (NoticeDefaults in
 * services/billing/Services/Alerts/NotificationRouting.cs). Email, WhatsApp
 * and push (9 October 2026) carry them; the ones about one person's own work
 * (a conversation or ticket given to them, a task of theirs) go to that person
 * by email and push. The inbox ones start on push alone.
 */
const MODULE_GROUP_LIST: { titleKey: string; kinds: string[] }[] = [
  { titleKey: 'settings.notifications.group.inbox', kinds: ['conversation_assigned', 'conversation_waiting', 'conversation_reply'] },
  { titleKey: 'settings.notifications.group.bookings', kinds: ['booking_request', 'booking_new', 'booking_changed'] },
  { titleKey: 'settings.notifications.group.tickets', kinds: ['ticket_new', 'ticket_assigned', 'ticket_breach', 'task_due'] },
  // A Facebook Page whose connection stopped working (16 October 2026).
  { titleKey: 'settings.notifications.group.channels', kinds: ['channel_disconnected'] },
  // A chat insights report ready, or a monthly one skipped (29 October 2026).
  { titleKey: 'settings.notifications.group.insights', kinds: ['insights_ready'] },
  // Flows: the ones picked in Suggest flows with AI are built (30 October 2026), and a step in a switched-on flow that
  // leads nowhere (2 November 2026).
  { titleKey: 'settings.notifications.group.flows', kinds: ['flows_ready', 'flow_loose_ends'] }
];
/** A kind shows once the SDK knows it, so a group listed ahead of its notice never offers a switch that does nothing. */
const MODULE_GROUPS: { titleKey: string; kinds: billing.NoticeKind[] }[] = MODULE_GROUP_LIST
  .map((g) => ({ titleKey: g.titleKey, kinds: g.kinds.filter(billing.isNoticeKind) }))
  .filter((g) => g.kinds.length > 0);
const MODULE_KINDS: ReadonlySet<string> = new Set(billing.NOTICE_KINDS);
const MODULE_CHANNELS: billing.NoticeChannel[] = ['email', 'whatsapp', 'push'];
const moduleChannelsOf = (kind: billing.NoticeKind): billing.NoticeChannel[] =>
  billing.PERSONAL_NOTICE_KINDS.includes(kind) ? ['email', 'push'] : MODULE_CHANNELS;

const MODULE_COPY: Partial<Record<string, { labelKey: string; hintKey: string }>> = {
  booking_request: { labelKey: 'settings.notifications.toggle.bookingRequest.label', hintKey: 'settings.notifications.toggle.bookingRequest.hint' },
  booking_new: { labelKey: 'settings.notifications.toggle.bookingNew.label', hintKey: 'settings.notifications.toggle.bookingNew.hint' },
  booking_changed: { labelKey: 'settings.notifications.toggle.bookingChanged.label', hintKey: 'settings.notifications.toggle.bookingChanged.hint' },
  ticket_new: { labelKey: 'settings.notifications.toggle.ticketNew.label', hintKey: 'settings.notifications.toggle.ticketNew.hint' },
  ticket_assigned: { labelKey: 'settings.notifications.toggle.ticketAssigned.label', hintKey: 'settings.notifications.toggle.ticketAssigned.hint' },
  ticket_breach: { labelKey: 'settings.notifications.toggle.ticketBreach.label', hintKey: 'settings.notifications.toggle.ticketBreach.hint' },
  task_due: { labelKey: 'settings.notifications.toggle.taskDue.label', hintKey: 'settings.notifications.toggle.taskDue.hint' },
  conversation_assigned: { labelKey: 'settings.notifications.toggle.conversationAssigned.label', hintKey: 'settings.notifications.toggle.conversationAssigned.hint' },
  conversation_waiting: { labelKey: 'settings.notifications.toggle.conversationWaiting.label', hintKey: 'settings.notifications.toggle.conversationWaiting.hint' },
  conversation_reply: { labelKey: 'settings.notifications.toggle.conversationReply.label', hintKey: 'settings.notifications.toggle.conversationReply.hint' },
  channel_disconnected: { labelKey: 'settings.notifications.toggle.channelDisconnected.label', hintKey: 'settings.notifications.toggle.channelDisconnected.hint' },
  insights_ready: { labelKey: 'settings.notifications.toggle.insightsReady.label', hintKey: 'settings.notifications.toggle.insightsReady.hint' },
  flows_ready: { labelKey: 'settings.notifications.toggle.flowsReady.label', hintKey: 'settings.notifications.toggle.flowsReady.hint' },
  flow_loose_ends: { labelKey: 'settings.notifications.toggle.flowLooseEnds.label', hintKey: 'settings.notifications.toggle.flowLooseEnds.hint' }
};

const CHANNEL_LABEL_KEYS: Record<string, string> = {
  email: 'settings.notifications.channel.email',
  whatsapp: 'settings.notifications.channel.whatsapp',
  webhook: 'settings.notifications.channel.webhook',
  in_app: 'settings.notifications.channel.inApp',
  push: 'settings.notifications.channel.push'
};

/*
 * Absent means on, on every channel. That is the server's default, and the
 * only safe one for an alert that says sending has stopped: somebody who has
 * never opened this page still hears about an empty wallet. The page has to
 * show that truthfully, which is only honest because nothing here is
 * interactive until the real values are in hand (see below).
 *
 * The rule itself is `identity.notificationAllowed` in sdk-api, the same one
 * the dispatcher applies, so this page cannot drift away from what actually
 * gets sent.
 */
function kindOn(prefs: Prefs, kind: string, channels: string[]): boolean {
  return channels.some((c) => identity.notificationAllowed(prefs, kind, c));
}

/** The smallest shape that says the same thing: `true` for every channel, `false` for none, the map in between. */
function canonical(map: ChannelMap, channels: string[]): PrefValue {
  const on = channels.filter((c) => map[c] !== false);
  if (on.length === channels.length) return true;
  if (on.length === 0) return false;
  const out: ChannelMap = {};
  for (const c of channels) out[c] = map[c] !== false;
  return out;
}

export default function SettingsNotificationsPage() {
  const t = useT();
  const qc = useQueryClient();
  const { user } = useAuth();

  const { data, isPending, error } = useQuery({
    queryKey: ['notification-prefs'],
    queryFn: () => identity.getNotificationPrefs()
  });

  const [prefs, setPrefs] = useState<Prefs>({});

  useEffect(() => {
    if (data?.data) setPrefs(data.data);
  }, [data]);

  const channels = useMemo(() => {
    const listed = data?.meta?.channels;
    return (listed && listed.length > 0 ? listed : FALLBACK_CHANNELS).filter((c) => !NOT_FOR_WALLET.has(c));
  }, [data]);

  /*
   * §4 item 5: preferences this console did not render were preserved on save
   * but never shown, so a toggle set elsewhere was invisible here while
   * silently surviving. They now render under their own name, and are
   * editable, because the server's `meta.kinds` tells us what exists.
   */
  const kinds = useMemo(() => {
    const listed = data?.meta?.kinds;
    const known = listed && listed.length > 0 ? listed : FALLBACK_KINDS;
    const extra = Object.keys(prefs).filter((k) => !known.includes(k));
    // The Bookings and Tickets kinds have their own groups below.
    return [...known, ...extra].filter((k) => !MODULE_KINDS.has(k));
  }, [data, prefs]);

  /*
   * §4 item 4: several independent switches behind one Save button is the
   * pattern people most often forget to press. Each change saves itself, and
   * reverts visibly if the write fails.
   */
  const save = useMutation({
    mutationFn: (next: Prefs) => identity.updateNotificationPrefs(next),
    onMutate: (next) => {
      const previous = prefs;
      setPrefs(next);
      return { previous };
    },
    onError: (e, _next, ctx) => {
      if (ctx?.previous) setPrefs(ctx.previous);
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.nav.notifications'));
    },
    // §4 item 10: the save never invalidated the query, so the cache and the
    // server could drift for the 30 s staleTime.
    onSettled: () => qc.invalidateQueries({ queryKey: ['notification-prefs'] })
  });

  function setKind(kind: string, on: boolean) {
    save.mutate({ ...prefs, [kind]: on });
  }

  function setChannel(kind: string, channel: string, on: boolean) {
    // Write the whole map, not one key: a stored `true` or a missing key both
    // mean "every channel", and flipping one of those to an object has to say
    // what the other three were.
    const map: ChannelMap = {};
    for (const c of channels) map[c] = identity.notificationAllowed(prefs, kind, c);
    map[channel] = on;
    save.mutate({ ...prefs, [kind]: canonical(map, channels) });
  }

  /** A notice kind as it starts: its default channels (the inbox ones on push alone). */
  function moduleDefault(kind: billing.NoticeKind): PrefValue {
    const map: ChannelMap = {};
    for (const c of moduleChannelsOf(kind)) map[c] = billing.NOTICE_KIND_DEFAULTS[kind].includes(c);
    return map;
  }

  const moduleAllowed = (kind: billing.NoticeKind, channel: string) => billing.noticeChannelAllowed(prefs, kind, channel);
  const moduleOn = (kind: billing.NoticeKind) => moduleChannelsOf(kind).some((c) => moduleAllowed(kind, c));

  function setModuleKind(kind: billing.NoticeKind, on: boolean) {
    save.mutate({ ...prefs, [kind]: on ? moduleDefault(kind) : false });
  }

  function setModuleChannel(kind: billing.NoticeKind, channel: string, on: boolean) {
    // Always the explicit map: `true` would mean every channel, which is not
    // what these kinds start on.
    const map: ChannelMap = {};
    for (const c of moduleChannelsOf(kind)) map[c] = moduleAllowed(kind, c);
    map[channel] = on;
    save.mutate({ ...prefs, [kind]: Object.values(map).some(Boolean) ? map : false });
  }

  function setAll(on: boolean) {
    const next: Prefs = { ...prefs };
    for (const kind of kinds) next[kind] = on;
    for (const kind of billing.NOTICE_KINDS) next[kind] = on ? moduleDefault(kind) : false;
    save.mutate(next);
  }

  const allOff = kinds.every((kind) => !kindOn(prefs, kind, channels)) && billing.NOTICE_KINDS.every((kind) => !moduleOn(kind));
  const labelOf = (kind: string) => (KIND_COPY[kind] ? t(KIND_COPY[kind].labelKey) : kind);
  const hintOf = (kind: string) => (KIND_COPY[kind] ? t(KIND_COPY[kind].hintKey) : undefined);
  const channelLabelOf = (channel: string) => (CHANNEL_LABEL_KEYS[channel] ? t(CHANNEL_LABEL_KEYS[channel]) : channel);

  return (
    <Stack gap="md">
      <PushDeviceCard />
      <Card withBorder data-guide="notifications-list">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
            <Stack gap={2}>
              <Title order={4}>{t('settings.nav.notifications')}</Title>
              {/* §4 item 7: the card was titled "Email notifications" and
                  never said which address they go to. It is four channels
                  now, so the title names the page and the caption keeps the
                  address. */}
              <Text role="caption" c="var(--rr-color-text-muted)">
                {t('settings.notifications.sentTo', { email: user?.email ?? '' })}
              </Text>
            </Stack>
            {/* §4 item 6: no way to turn everything off. */}
            <Button size="xs" variant="default" onClick={() => setAll(allOff)} disabled={isPending || !!error}>
              {allOff ? t('settings.notifications.allOn') : t('settings.notifications.allOff')}
            </Button>
          </Group>

          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.notifications.lead')}</Text>

          {/*
            §4 item 8: an unverified user could set notifications that will
            never arrive, on the one page where that matters most.
          */}
          {user && !user.email_verified && (
            <Alert color="warning">
              {t('settings.notifications.unverified')}{' '}
              <Link to="/auth/verify-email">{t('settings.hub.emailUnverified')}</Link>
            </Alert>
          )}

          {/*
            §4 items 1 and 2, a data-loss bug rather than a cosmetic one: the
            switches read the preferences against an initially empty object,
            so the first paint claimed everything was off, and a failed GET
            was never detected. A user quick enough to toggle one switch and
            press Save wrote falses over trues. Nothing is interactive until
            the real values are in hand.
          */}
          {error && <Alert color="error" role="alert">{t('settings.notifications.loadError')}</Alert>}

          {isPending && !error && (
            <Stack gap="sm">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} height={64} />)}</Stack>
          )}

          {!isPending && !error && (
            /* §4 item 13: switches with no grouping, so a screen reader heard
               a list of unrelated controls. */
            <div role="group" aria-label={t('settings.nav.notifications')}>
              <Stack gap="md">
                {kinds.map((kind) => {
                  const on = kindOn(prefs, kind, channels);
                  return (
                    <Stack key={kind} gap={6}>
                      <Switch
                        label={labelOf(kind)}
                        description={hintOf(kind)}
                        checked={on}
                        onChange={(next: boolean) => setKind(kind, next)}
                        disabled={save.isPending}
                      />
                      {/* The channel choice sits under the notification it
                          belongs to, and goes flat when the notification is
                          off, so "on, by email only" is one reading rather
                          than two. */}
                      <div role="group" aria-label={t('settings.notifications.channels.aria', { name: labelOf(kind) })} style={{ paddingLeft: 44 }}>
                        <Group gap="md" wrap="wrap">
                          {channels.map((channel) => (
                            <Checkbox
                              key={channel}
                              size="xs"
                              label={channelLabelOf(channel)}
                              checked={on && identity.notificationAllowed(prefs, kind, channel)}
                              onChange={(next: boolean) => setChannel(kind, channel, next)}
                              disabled={!on || save.isPending}
                            />
                          ))}
                        </Group>
                      </div>
                    </Stack>
                  );
                })}
              </Stack>
            </div>
          )}

          {!isPending && !error && MODULE_GROUPS.map((group) => (
            <div key={group.titleKey} role="group" aria-label={t(group.titleKey)}>
              <Stack gap="md">
                <Title order={5}>{t(group.titleKey)}</Title>
                {group.kinds.map((kind) => {
                  const on = moduleOn(kind);
                  const copy = MODULE_COPY[kind];
                  const label = copy ? t(copy.labelKey) : kind;
                  return (
                    <Stack key={kind} gap={6}>
                      <Switch
                        label={label}
                        description={copy ? t(copy.hintKey) : undefined}
                        checked={on}
                        onChange={(next: boolean) => setModuleKind(kind, next)}
                        disabled={save.isPending}
                      />
                      <div role="group" aria-label={t('settings.notifications.channels.aria', { name: label })} style={{ paddingLeft: 44 }}>
                        <Group gap="md" wrap="wrap">
                          {moduleChannelsOf(kind).map((channel) => (
                            <Checkbox
                              key={channel}
                              size="xs"
                              label={channelLabelOf(channel)}
                              checked={on && moduleAllowed(kind, channel)}
                              onChange={(next: boolean) => setModuleChannel(kind, channel, next)}
                              disabled={!on || save.isPending}
                            />
                          ))}
                        </Group>
                      </div>
                    </Stack>
                  );
                })}
              </Stack>
            </div>
          ))}

          {!isPending && !error && (
            <Stack gap={4}>
              {/* Under the controls, never between a label and its input. One
                  line for the whole set rather than the same sentence
                  repeated beside forty checkboxes. */}
              <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.notifications.channels.note')}</Text>
              <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.notifications.autosave')}</Text>
            </Stack>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
